/**
 * KundenentscheidDialog — record the Kundenentscheid on the customer's behalf (P2 Placement Slice 12,
 * Tab 4c). Angenommen (offer pick + E5 deviation reason + Praemie/Beginn) / Alle abgelehnt (E13) /
 * Rueckfrage. The server owns the gate; the dialog only collects.
 */

import { useState } from 'react';
import {
  ABWEICHUNG_GRUND_MIN,
  KUNDENENTSCHEID_ABWEICHEND_LABEL,
  KUNDENENTSCHEID_ABWEICHUNG_HINWEIS,
  KUNDENENTSCHEID_E13_HINWEIS,
  PROMOTION_HINWEIS,
  PROMOTION_NEUE_POLICE_HINWEIS,
  RUECKFRAGE_TEXT_MIN,
  type KundenentscheidErfassenBody,
  type KundenfreigabeResponse,
  type PlacementDecisionArt,
} from '@brokly/shared';
import { Dialog } from '../administration/components';
import { Button } from '../../components/ui/Button';
import { SegmentedControl } from '../../components/ui/SegmentedControl';

const ERROR_TEXT: Record<string, string> = {
  abweichung_grund_required: KUNDENENTSCHEID_ABWEICHUNG_HINWEIS,
  offer_not_in_report: 'Die gewaehlte Offerte gehoert nicht zum zugestellten Vergleich',
  bereits_entschieden: 'Der Kundenentscheid wurde bereits dokumentiert',
  too_many_objects: 'Die Police hat bereits die maximale Anzahl versicherter Objekte',
};

export function KundenentscheidDialog({
  open,
  onClose,
  facts,
  onErfassen,
  saving,
  error,
}: {
  open: boolean;
  onClose: () => void;
  facts: KundenfreigabeResponse;
  onErfassen: (body: KundenentscheidErfassenBody) => void;
  saving: boolean;
  error: { code?: string } | null;
}) {
  const empfehlung = facts.offerten.find((o) => o.ist_empfehlung) ?? facts.offerten[0];
  const [art, setArt] = useState<PlacementDecisionArt>('angenommen');
  const [offerId, setOfferId] = useState<string>(empfehlung?.offer_document_id ?? '');
  const [abweichungGrund, setAbweichungGrund] = useState('');
  const [praemie, setPraemie] = useState<string>(empfehlung ? String(empfehlung.praemie_jahr ?? '') : '');
  const [beginn, setBeginn] = useState('');
  const [text, setText] = useState('');
  const [responseChannel, setResponseChannel] = useState<'email' | 'telefon' | 'persoenlich'>('email');
  const [entscheiderName, setEntscheiderName] = useState('');

  const chosen = facts.offerten.find((o) => o.offer_document_id === offerId);
  const abweichend = art === 'angenommen' && empfehlung && offerId !== empfehlung.offer_document_id;
  const neugeschaeft = facts.offerten.length > 0 && facts.freigabe !== null; // promotion note; exact flag unknown client-side

  const canSubmit =
    !saving &&
    entscheiderName.trim().length >= 1 &&
    (art === 'angenommen'
      ? !!offerId && (!abweichend || abweichungGrund.trim().length >= ABWEICHUNG_GRUND_MIN)
      : text.trim().length >= RUECKFRAGE_TEXT_MIN);

  const submit = (): void => {
    const base = { art, response_channel: responseChannel, entscheider_name: entscheiderName.trim() };
    if (art === 'angenommen') {
      onErfassen({
        ...base,
        gewaehlte_offer_id: offerId,
        abweichung_grund: abweichend ? abweichungGrund.trim() : null,
        praemie_jahr: praemie.trim() ? Number(praemie) : null,
        versicherungsbeginn: beginn.trim() ? beginn.trim() : null,
      });
    } else {
      onErfassen({ ...base, text: text.trim() });
    }
  };

  const errorText = error?.code ? (ERROR_TEXT[error.code] ?? `Entscheid fehlgeschlagen (${error.code})`) : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Kundenentscheid erfassen"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button disabled={!canSubmit} onClick={submit} data-testid="kundenentscheid-submit">Entscheid dokumentieren</Button>
        </div>
      }
    >
      <div className="space-y-3 text-sm" data-testid="kundenentscheid-dialog">
        <SegmentedControl
          ariaLabel="Entscheid-Art"
          value={art}
          onValueChange={(v) => setArt(v as PlacementDecisionArt)}
          options={[
            { value: 'angenommen', label: 'Angenommen' },
            { value: 'abgelehnt', label: 'Alle abgelehnt' },
            { value: 'rueckfrage', label: 'Rueckfrage' },
          ]}
        />

        {art === 'angenommen' && (
          <>
            <label className="block">
              <span className="text-xs text-muted-foreground">Gewaehlte Offerte</span>
              <select
                className="mt-1 w-full rounded-md border border-border p-2"
                value={offerId}
                onChange={(e) => {
                  setOfferId(e.target.value);
                  const o = facts.offerten.find((x) => x.offer_document_id === e.target.value);
                  setPraemie(o ? String(o.praemie_jahr ?? '') : '');
                }}
                data-testid="kundenentscheid-offer"
              >
                {facts.offerten.map((o) => (
                  <option key={o.offer_document_id} value={o.offer_document_id}>
                    {o.rang}. {o.carrier_name} - Score {o.score_total} - CHF {o.praemie_jahr ?? '-'}
                    {o.ist_empfehlung ? ' (Empfehlung)' : ''}
                  </option>
                ))}
              </select>
            </label>
            {abweichend && (
              <label className="block">
                <span className="text-xs font-medium text-amber-700">{KUNDENENTSCHEID_ABWEICHEND_LABEL}</span>
                <textarea
                  className="mt-1 w-full rounded-md border border-amber-300 p-2"
                  placeholder="Begruendung der Abweichung"
                  value={abweichungGrund}
                  onChange={(e) => setAbweichungGrund(e.target.value)}
                  data-testid="kundenentscheid-abweichung"
                />
                <span className="text-xs text-muted-foreground">{KUNDENENTSCHEID_ABWEICHUNG_HINWEIS}</span>
              </label>
            )}
            <label className="block">
              <span className="text-xs text-muted-foreground">Jahrespraemie (CHF)</span>
              <input className="mt-1 w-full rounded-md border border-border p-2" type="number" min={0} value={praemie} onChange={(e) => setPraemie(e.target.value)} data-testid="kundenentscheid-praemie" />
            </label>
            <label className="block">
              <span className="text-xs text-muted-foreground">Versicherungsbeginn</span>
              <input className="mt-1 w-full rounded-md border border-border p-2" type="date" value={beginn} onChange={(e) => setBeginn(e.target.value)} data-testid="kundenentscheid-beginn" />
            </label>
            <p className="text-xs text-muted-foreground">{neugeschaeft ? PROMOTION_HINWEIS : PROMOTION_NEUE_POLICE_HINWEIS}</p>
          </>
        )}

        {art === 'abgelehnt' && (
          <>
            <label className="block">
              <span className="text-xs text-muted-foreground">Grund</span>
              <textarea className="mt-1 w-full rounded-md border border-border p-2" value={text} onChange={(e) => setText(e.target.value)} data-testid="kundenentscheid-text" />
            </label>
            <p className="text-xs text-muted-foreground">{KUNDENENTSCHEID_E13_HINWEIS}</p>
          </>
        )}

        {art === 'rueckfrage' && (
          <label className="block">
            <span className="text-xs text-muted-foreground">Rueckfrage des Kunden</span>
            <textarea className="mt-1 w-full rounded-md border border-border p-2" value={text} onChange={(e) => setText(e.target.value)} data-testid="kundenentscheid-text" />
          </label>
        )}

        <div className="flex gap-2">
          <label className="block flex-1">
            <span className="text-xs text-muted-foreground">Rueckmeldung via</span>
            <select className="mt-1 w-full rounded-md border border-border p-2" value={responseChannel} onChange={(e) => setResponseChannel(e.target.value as 'email' | 'telefon' | 'persoenlich')} data-testid="kundenentscheid-channel">
              <option value="email">E-Mail-Antwort</option>
              <option value="telefon">Telefon</option>
              <option value="persoenlich">Persoenlich</option>
            </select>
          </label>
          <label className="block flex-1">
            <span className="text-xs text-muted-foreground">Entscheider (Name, Funktion)</span>
            <input className="mt-1 w-full rounded-md border border-border p-2" value={entscheiderName} onChange={(e) => setEntscheiderName(e.target.value)} data-testid="kundenentscheid-name" />
          </label>
        </div>

        {chosen && art === 'angenommen' && <p className="text-xs text-muted-foreground">Gewaehlt: {chosen.carrier_name}</p>}
        {errorText && <p className="text-sm text-red-600" data-testid="kundenentscheid-error">{errorText}</p>}
      </div>
    </Dialog>
  );
}
